import { useCallback, useContext, useEffect, useState } from 'react';
import { UserContext } from '../UserContext.jsx';

/** Auth role for Entra group "ACAA WebApp Access" (996e2a04-f99a-4d5f-a966-aa2cd4284941). */
const DEFAULT_VIEWERS_GROUPS = ['general', 'webaccess'];
const POLL_MS = 15000;
const COLLAPSED_POLL_MS = 60000;

const formatSeen = (iso) => {
	if (!iso) return '';
	const ms = Date.now() - new Date(iso).getTime();
	if (Number.isNaN(ms) || ms < 0) return 'just now';
	const mins = Math.floor(ms / 60000);
	if (mins < 1) return 'just now';
	if (mins === 1) return '1 minute ago';
	return `${mins} minutes ago`;
};

const viewersGroups = () => {
	const raw = import.meta.env.VITE_VIEWERS_GROUP_ACCESS;
	if (typeof raw === 'string' && raw.trim()) {
		return raw.split(',').map((g) => g.trim().toLowerCase()).filter(Boolean);
	}
	return DEFAULT_VIEWERS_GROUPS;
};

/** True when UserInfo groups include ACAA WebApp Access (`general` / `webaccess`). */
export const canSeeActiveViewers = (userInfo) => {
	const groups = Array.isArray(userInfo?.groups) ? userInfo.groups : [];
	const allowed = viewersGroups();
	return groups.some((g) => allowed.includes(String(g).trim().toLowerCase()));
};

export const ActiveViewersPanel = () => {
	const { userInfo, apiCall } = useContext(UserContext);
	const [open, setOpen] = useState(false);
	const [viewers, setViewers] = useState([]);
	const [error, setError] = useState(null);
	const [loading, setLoading] = useState(false);

	const allowed = canSeeActiveViewers(userInfo);

	const fetchViewers = useCallback(async () => {
		if (!apiCall || !allowed) return;
		setLoading(true);
		try {
			const res = await apiCall('activeviewers');
			const list = Array.isArray(res?.data?.viewers) ? res.data.viewers : [];
			setViewers(list);
			setError(null);
		} catch (err) {
			console.error('Failed to load active viewers', err);
			setError('Unable to load viewers');
		} finally {
			setLoading(false);
		}
	}, [apiCall, allowed]);

	useEffect(() => {
		if (!allowed) return undefined;

		let intervalId;

		const tick = () => {
			if (document.visibilityState === 'hidden') return;
			fetchViewers();
		};

		const startInterval = () => {
			if (intervalId) clearInterval(intervalId);
			const ms = open ? POLL_MS : COLLAPSED_POLL_MS;
			intervalId = setInterval(tick, ms);
		};

		const onVisibility = () => {
			if (document.visibilityState === 'visible') {
				tick();
				startInterval();
			}
			else if (intervalId) {
				clearInterval(intervalId);
				intervalId = undefined;
			}
		};

		tick();
		if (document.visibilityState === 'visible') {
			startInterval();
		}
		document.addEventListener('visibilitychange', onVisibility);

		return () => {
			if (intervalId) clearInterval(intervalId);
			document.removeEventListener('visibilitychange', onVisibility);
		};
	}, [allowed, fetchViewers, open]);

	if (!allowed) return null;

	const toggleIcon = open ? 'pi-chevron-left' : 'pi-chevron-right';
	const empty = viewers.length === 0;

	return (
		<aside className={`viewers-panel ${open ? 'is-open' : 'is-collapsed'}`}>
			<button
				type="button"
				className="viewers-panel-toggle"
				onClick={() => setOpen((v) => !v)}
				aria-expanded={open}
				aria-controls="viewers-panel-body"
				aria-label={
					open
						? 'Hide active viewers'
						: `Show active viewers${viewers.length ? ` (${viewers.length})` : ''}`
				}
			>
				<span className="viewers-panel-title">
					Active Viewers
					<span className={`viewers-count ${viewers.length ? 'has-items' : ''}`}>
						{viewers.length}
					</span>
				</span>
				<i className={`pi ${toggleIcon}`} aria-hidden />
			</button>

			{open && (
				<div id="viewers-panel-body" className="viewers-panel-body">
					{loading && empty ? (
						<div className="viewers-empty">Loading…</div>
					) : error ? (
						<div className="viewers-empty">{error}</div>
					) : empty ? (
						<div className="viewers-empty">No active viewers</div>
					) : (
						<ul className="viewers-list">
							{viewers.map((viewer) => (
								<li key={viewer.email || viewer.displayName}>
									<div className="viewers-name">
										{viewer.displayName || viewer.email}
									</div>
									<div className="viewers-email">{viewer.email}</div>
									{(viewer.title || viewer.lastSeenUtc) && (
										<div className="viewers-meta">
											{viewer.title ? <span>{viewer.title}</span> : null}
											{viewer.lastSeenUtc ? (
												<span>{formatSeen(viewer.lastSeenUtc)}</span>
											) : null}
										</div>
									)}
								</li>
							))}
						</ul>
					)}
				</div>
			)}
		</aside>
	);
};
